import { useRef } from 'react';
import cx from 'classnames';

import { FacingMode, InviteeSchema, Layout, LiveEventAction, OnStageUser, UserRole } from 'types';
import { MAX_STAGE_SEATS } from 'utils';
import {
  eventSessionSelector,
  filledSeatsCountSelector,
  inviteesSelector,
  pendingUsersSelector,
  userSelector
} from 'store/selectors';
import { useLiveEventActions, useStore } from 'hooks';
import { Text } from 'components';
import { MicrophoneOnIcon } from 'components/Icon';

import { useLiveEvent } from './LiveEvent.context';
import StageSeat from './LiveEvent.StageSeat';

import styles from './LiveEvent.module.scss';

export type OnStageProps = {
  users?: OnStageUser[];
  facingMode?: FacingMode;
  layout?: Layout;
  activeSpeakerUsername?: string;
  activeSpeakerName?: string;
  activeSpeakerVideoMuted?: boolean;
  onOpenInviteToStage?(invitee?: InviteeSchema, seatIndex?: number): void;
  onSelectActiveSpeaker?(username: string): void;
};

const OnStage = ({
  users = [],
  facingMode = FacingMode.FRONT,
  layout = Layout.ACTIVE_SPEAKER,
  activeSpeakerUsername,
  activeSpeakerName,
  activeSpeakerVideoMuted = false,
  onOpenInviteToStage,
  onSelectActiveSpeaker
}: OnStageProps) => {
  const liveEvent = useLiveEvent();
  const eventSession = useStore(eventSessionSelector(liveEvent.eventCode));
  const localUser = useStore(userSelector(liveEvent.eventCode, eventSession.username));
  const { filledSeatsCount, invitees, pendingUsers } = useStore(state => ({
    filledSeatsCount: filledSeatsCountSelector(liveEvent.eventCode)(state),
    invitees: inviteesSelector(liveEvent.eventCode)(state),
    pendingUsers: pendingUsersSelector(liveEvent.eventCode)(state)
  }));
  const liveEventActions = useLiveEventActions(liveEvent.eventCode);

  const containerRef = useRef<HTMLDivElement>(null);
  const gridViewEnabled = layout === Layout.GRID_VIEW;
  const canAddMore =
    filledSeatsCount < MAX_STAGE_SEATS &&
    !gridViewEnabled &&
    liveEventActions.hasPermission(LiveEventAction.GENERATE_INVITATION_LINK);

  const getLayout = () => (gridViewEnabled ? 'grid' : null);

  const getGridMode = () => {
    const numberOfSpeakers = ['two', 'three', 'four', 'five', 'six'];
    const joinedUsersCount = users.filter(user => user.joined)?.length;

    return numberOfSpeakers[joinedUsersCount - 2];
  };

  const renderHeader = () => {
    if (!activeSpeakerName || activeSpeakerVideoMuted) return null;

    const content = (
      <div className={styles.header}>
        <MicrophoneOnIcon />
        <Text className={styles.title} weight="bold" size="h4">
          {activeSpeakerName}
        </Text>
      </div>
    );

    switch (localUser.role) {
      case UserRole.HOST:
      case UserRole.MODERATOR:
      case UserRole.SPEAKER:
        return content;

      case UserRole.VIEWER:
        return users.length > 0 ? content : null;

      default:
        return null;
    }
  };

  const renderAddMore = () => {
    const handleOnAdd = () => onOpenInviteToStage?.(undefined, users.length + invitees.length + 1);

    return <StageSeat type="empty" onClick={handleOnAdd} />;
  };

  return (
    <div
      className={cx(styles.onStage, {
        [styles[`layout--${getLayout()}`]]: gridViewEnabled,
        [styles[`grid-mode--${getGridMode()}`]]: gridViewEnabled
      })}
    >
      <div className={styles.container}>
        {renderHeader()}
        <div ref={containerRef} className={styles.speakersContainer}>
          <ul className={styles.speakers}>
            {users.map((user, index) => {
              const type = !user.joined ? 'pending' : 'filled';
              const seatIndex = index;

              if (type === 'pending' && gridViewEnabled) return null;

              const onClick = () => {
                if (type === 'filled') {
                  onSelectActiveSpeaker?.(user.username);
                }
              };

              return (
                <li key={user.username}>
                  <StageSeat
                    seatIndex={seatIndex}
                    isGridItem={gridViewEnabled}
                    withAudioIcon
                    type={type}
                    user={user}
                    activeSpeakerUsername={activeSpeakerUsername}
                    userType={eventSession.username === user.username ? 'local' : 'remote'}
                    facingMode={facingMode}
                    onClick={onClick}
                  />
                </li>
              );
            })}

            {[UserRole.HOST, UserRole.MODERATOR].includes(localUser.role) &&
              !gridViewEnabled &&
              invitees.map((invitee, inviteeIndex) => {
                const type = 'pending';
                const seatIndex = users.length + inviteeIndex + 1;
                const onClick = () => {
                  onOpenInviteToStage?.(
                    {
                      eventCode: liveEvent.eventCode,
                      inviteeCode: invitee.inviteeCode,
                      role: invitee.role,
                      name: invitee.name
                    },
                    seatIndex
                  );
                };

                return (
                  <li key={invitee.inviteeCode}>
                    <StageSeat seatIndex={seatIndex} type={type} onClick={onClick} />
                  </li>
                );
              })}

            {[UserRole.HOST, UserRole.MODERATOR].includes(localUser.role) &&
              !gridViewEnabled &&
              pendingUsers.map(pendingUser => (
                <li key={pendingUser.username}>
                  <StageSeat type="pending" />
                </li>
              ))}

            {canAddMore && <li>{renderAddMore()}</li>}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default OnStage;
